import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const caseStudies = [
  {
    heading: ["How Lincoln AIM","Helped Careem","Scale","Organizational","Elasticity"],
    description:
      "Careem, the Middle East's leading super app, faced challenges with scaling their workforce while maintaining a world-class candidate experience. With rapid expansion into new markets and an increasing demand for specialized talent, traditional recruitment methods weren't enough. Download the PDF to discover how Lincoln AIM partnered with Careem to redefine the hiring process and bring top-tier talent into key positions, driving impressive results.",
    file: '/case-studies/Case Study 1.pdf',
    readTime: '5 min read',
  },
  {
    heading: ["How Lincoln AIM","Helped Client X","Scale Elite Tech","Hiring"],
    description:
      "In just 12 months, Client X, a prestigious US hedge fund, had to hire more than 20 top-tier quantitative researchers and engineers. Conventional hiring practices were failing in a very competitive sector. Download the PDF to learn how Lincoln AIM and Client X collaborated to revolutionize their people strategy, streamlining the hiring procedure and guaranteeing a smooth cultural fit for their innovative employees.",
    file: '/case-studies/Case Study 2.pdf',
    readTime: '7 min read',
  }
];

const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.1 } }
};
const mobileTextVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut", staggerChildren: 0.01 } }
};
const letterVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.15, ease: "easeOut" } } };

function CaseStudyHeading({ lines, className = "" }) {
  return (
    <>
      <motion.h3
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={mobileTextVariants}
        className={`block lg:hidden text-2xl md:text-3xl font-semibold italic text-gray-800 mb-4 whitespace-normal leading-tight ${className}`}
      >
        {lines.join(' ').split('').map((char, i) => (
          <motion.span key={i} variants={letterVariants} className="inline-block">
            {char === " " ? "\u00A0" : char}
          </motion.span>
        ))}
      </motion.h3>

      <motion.h3
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={textVariants}
        className={`hidden lg:block text-2xl md:text-3xl lg:text-4xl font-semibold italic text-gray-800 mb-4 lg:mb-6 whitespace-pre-line leading-tight ${className}`}
      >
        {lines.map((line, li) => (
          <motion.div key={li} variants={letterVariants}>
            {line.split('').map((c, ci) => (
              <motion.span key={ci} variants={letterVariants} className="inline-block">
                {c === " " ? "\u00A0" : c}
              </motion.span>
            ))}
          </motion.div>
        ))}
      </motion.h3>
    </>
  );
}

function DownloadButton({ file, className = "" }) {
  return (
    <motion.button
      whileHover={{ scale: 0.95 }}
      whileTap={{ scale: 0.9 }}
      className={`flex items-center border border-gray-400 rounded-lg px-5 py-2 text-gray-800 hover:bg-gray-100 transition-colors text-base font-medium mb-3 mt-2 w-full sm:w-auto ${className}`}
      onClick={() => {
        const link = document.createElement('a');
        link.href = file;
        link.download = file.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }}
    >
      <motion.svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </motion.svg>
      Read More
    </motion.button>
  );
}

export default function CaseStudySection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section ref={sectionRef} className="bg-white py-20">
      <div className="max-w-5xl mx-auto px-6 md:px-10 w-full">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-left border-b-2 border-gray-700 pb-2"
        >
          Case Studies
        </motion.h2>

        <div className="flex flex-col gap-16">
          {/* First Block */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex flex-col lg:flex-row gap-4 items-stretch overflow-hidden"
          >
            {/* Watermark */}
            <div
              className="absolute inset-x-0 top-0 bottom-12 opacity-30 -z-0 bg-no-repeat bg-center bg-contain"
              style={{ backgroundImage: "url('/images/Org1.png')" }}
            />
            <div className="relative z-10 flex flex-col lg:flex-row gap-4 items-stretch w-full">
              <div className="lg:w-5/12 flex flex-col items-start justify-center">
                <CaseStudyHeading lines={caseStudies[0].heading} className="text-left" />
                <DownloadButton file={caseStudies[0].file} className="text-left" />
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-sm text-gray-500 mt-1 mb-2 text-left"
                >
                  Case Study • {caseStudies[0].readTime}
                </motion.span>
              </div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="lg:w-9/12 flex items-center"
              >
                <p className="text-gray-700 text-base md:text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                  {caseStudies[0].description}
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* Second Block */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col lg:flex-row gap-4 items-stretch"
          >
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:w-8/12 flex items-center order-2 lg:order-1"
            >
              <p className="text-gray-700 text-base md:text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                {caseStudies[1].description}
              </p>
            </motion.div>

            <div className="lg:w-5/12 flex flex-col items-start lg:items-end justify-center order-1 lg:order-2">
              <CaseStudyHeading lines={caseStudies[1].heading} className="text-left lg:text-right" />
              <DownloadButton file={caseStudies[1].file} className="text-left lg:text-right" />
              <motion.span
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-sm text-gray-500 mt-1 mb-2 text-left lg:text-right"
              >
                Case Study • {caseStudies[1].readTime}
              </motion.span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
